/* ============================================================================
 * Real-World Double-Star Resolving & Eyepiece Calculator
 * Ontario Telescope & Accessories
 *
 * All math runs in the browser. Nothing is uploaded.
 *
 * The resolving model goes beyond Dawes/Rayleigh: it layers atmospheric
 * seeing, magnitude-difference glare, central-obstruction ring energy,
 * colour contrast relief and observer experience on top of the theoretical
 * diffraction limit. See the on-page guide for the reasoning and caveats.
 *
 * CSS: assets/double-star-calculator.css
 * Requires Aladin Lite v3 (loaded by the section) for the real-sky preview.
 * ==========================================================================*/
(function () {
  'use strict';

  /* ----------------------------------------------------------------------
   * DATA — telescope presets (aperture in mm, focal length in mm,
   * central obstruction as a fraction of aperture diameter). Obstruction
   * figures are typical for the class; every field stays editable.
   * -------------------------------------------------------------------- */
  var TELESCOPES = [
    { id: 'manual', label: 'Manual entry / other', type: 'manual', ap: null, fl: null, obs: 0 },

    { id: 'ref60',  label: 'Refractor 60 mm (f/6–f/12)',  type: 'refractor', ap: 60,  fl: 420,  obs: 0 },
    { id: 'ref70',  label: 'Refractor 70 mm',                  type: 'refractor', ap: 70,  fl: 420,  obs: 0 },
    { id: 'ref80',  label: 'Refractor 80 mm (f/6 apo)',        type: 'refractor', ap: 80,  fl: 480,  obs: 0 },
    { id: 'ref100', label: 'Refractor 100 mm (f/7 apo)',       type: 'refractor', ap: 100, fl: 700,  obs: 0 },
    { id: 'ref120', label: 'Refractor 120 mm',                 type: 'refractor', ap: 120, fl: 840,  obs: 0 },
    { id: 'ref150', label: 'Refractor 150 mm',                 type: 'refractor', ap: 150, fl: 1200, obs: 0 },

    { id: 'nt114',  label: 'Newtonian 114 mm',                 type: 'newtonian', ap: 114, fl: 900,  obs: 0.22 },
    { id: 'nt130',  label: 'Newtonian 130 mm',                 type: 'newtonian', ap: 130, fl: 650,  obs: 0.24 },
    { id: 'nt150',  label: 'Newtonian / 6" Dob',               type: 'newtonian', ap: 150, fl: 1200, obs: 0.20 },
    { id: 'nt200',  label: 'Newtonian / 8" Dob',               type: 'newtonian', ap: 203, fl: 1200, obs: 0.22 },
    { id: 'nt250',  label: 'Newtonian / 10" Dob',              type: 'newtonian', ap: 254, fl: 1200, obs: 0.23 },
    { id: 'nt300',  label: 'Newtonian / 12" Dob',              type: 'newtonian', ap: 305, fl: 1500, obs: 0.24 },
    { id: 'nt400',  label: 'Newtonian / 16" Dob',              type: 'newtonian', ap: 406, fl: 1800, obs: 0.25 },

    { id: 'sct5',   label: 'Celestron C5 SCT',                 type: 'SCT', ap: 127, fl: 1250, obs: 0.32 },
    { id: 'sct6',   label: 'Celestron C6 / EdgeHD 6',          type: 'SCT', ap: 150, fl: 1500, obs: 0.32 },
    { id: 'sct8',   label: 'Celestron C8 / EdgeHD 8',          type: 'SCT', ap: 203, fl: 2032, obs: 0.34 },
    { id: 'sct925', label: 'Celestron C9.25 / EdgeHD 9.25',    type: 'SCT', ap: 235, fl: 2350, obs: 0.36 },
    { id: 'sct11',  label: 'Celestron C11 / EdgeHD 11',        type: 'SCT', ap: 280, fl: 2800, obs: 0.36 },
    { id: 'sct14',  label: 'Celestron C14 / EdgeHD 14',        type: 'SCT', ap: 356, fl: 3910, obs: 0.36 },

    { id: 'mak90',  label: 'Maksutov 90 mm',                   type: 'Mak', ap: 90,  fl: 1250, obs: 0.34 },
    { id: 'mak102', label: 'Maksutov 102 mm',                  type: 'Mak', ap: 102, fl: 1300, obs: 0.32 },
    { id: 'mak127', label: 'Maksutov 127 mm',                  type: 'Mak', ap: 127, fl: 1500, obs: 0.34 },
    { id: 'mak150', label: 'Maksutov 150 mm',                  type: 'Mak', ap: 150, fl: 1800, obs: 0.36 }
  ];

  /* ----------------------------------------------------------------------
   * DATA — a curated set of showpiece and test double stars.
   * ra: J2000 right ascension in HOURS. dec: J2000 declination in DEGREES.
   * sep: current separation in arcseconds. m1/m2: component magnitudes.
   * col: short colour description. Separations for binaries drift over time;
   * treat them as recent-epoch values and confirm against a current catalogue.
   * -------------------------------------------------------------------- */
  var DOUBLES = [
    { id:'albireo', name:'Albireo (β Cygni)',        con:'Cygnus',        ra:19.5120, dec:27.9597, sep:34.7, m1:3.4, m2:4.7, col:'gold + blue', note:'The classic colour-contrast pair. Wide and easy.' },
    { id:'mizar',   name:'Mizar (ζ Ursae Majoris)',  con:'Ursa Major',    ra:13.3987, dec:54.9254, sep:14.4, m1:2.2, m2:3.9, col:'white + white', note:'Naked-eye Mizar/Alcor; the telescopic split is Mizar A/B.' },
    { id:'polaris', name:'Polaris (α UMi)',          con:'Ursa Minor',    ra:2.5303,  dec:89.2641, sep:18.2, m1:2.0, m2:9.0, col:'yellow + white', note:'Bright primary, faint companion — a good glare test.' },
    { id:'almach',  name:'Almach (γ Andromedae)',    con:'Andromeda',     ra:2.0650,  dec:42.3297, sep:9.6,  m1:2.3, m2:5.0, col:'gold + blue', note:'Often called the winter Albireo.' },
    { id:'castor',  name:'Castor (α Geminorum)',     con:'Gemini',        ra:7.5766,  dec:31.8883, sep:5.4,  m1:1.9, m2:3.0, col:'white + white', note:'Slowly widening bright pair.' },
    { id:'algieba', name:'Algieba (γ Leonis)',       con:'Leo',           ra:10.3328, dec:19.8415, sep:4.6,  m1:2.4, m2:3.6, col:'gold + gold', note:'Beautiful matched golden pair.' },
    { id:'izar',    name:'Izar (ε Boötis)',     con:'Boötes',   ra:14.7498, dec:27.0742, sep:2.9,  m1:2.6, m2:4.8, col:'yellow + blue', note:'"Pulcherrima" — a fine, tight contrast pair.' },
    { id:'porrima', name:'Porrima (γ Virginis)',     con:'Virgo',         ra:12.6943, dec:-1.4494, sep:3.6,  m1:3.5, m2:3.5, col:'white + white', note:'Fast binary; separation changes noticeably over years.' },
    { id:'doublede',name:'Double-Double ε¹ Lyrae',con:'Lyra',        ra:18.7377, dec:39.6703, sep:2.3,  m1:5.0, m2:6.1, col:'white + white', note:'One of two tight pairs in the famous quadruple.' },
    { id:'doubledf',name:'Double-Double ε² Lyrae',con:'Lyra',        ra:18.7440, dec:39.6147, sep:2.4,  m1:5.2, m2:5.5, col:'white + white', note:'The other pair — splitting both needs steady air.' },
    { id:'rigel',   name:'Rigel (β Orionis)',        con:'Orion',         ra:5.2423,  dec:-8.2016, sep:9.5,  m1:0.1, m2:6.8, col:'blue-white', note:'Faint companion beside a brilliant primary.' },
    { id:'antares', name:'Antares (α Scorpii)',      con:'Scorpius',      ra:16.4901, dec:-26.4320,sep:2.6,  m1:1.0, m2:5.4, col:'red + green', note:'Low-altitude glare test; companion looks greenish.' },
    { id:'sirius',  name:'Sirius (α Canis Majoris)', con:'Canis Major',   ra:6.7525,  dec:-16.7161,sep:11.3, m1:-1.46,m2:8.44,col:'white + white', note:'The Pup. Aperture is fine — the ~10-mag glare is the wall.' },
    { id:'zetaher', name:'ζ Herculis',               con:'Hercules',      ra:16.6882, dec:31.6020, sep:1.5,  m1:2.9, m2:5.5, col:'yellow + orange', note:'Tight fast binary; a genuine aperture test.' },
    { id:'32ori',   name:'32 Orionis',                    con:'Orion',         ra:5.5108,  dec:5.9486,  sep:1.4,  m1:4.5, m2:5.8, col:'white + white', note:'Sub-arcsecond-class test for 100–150 mm.' },
    { id:'pi aql',  name:'π Aquilae',                con:'Aquila',        ra:19.4772, dec:11.8067, sep:1.4,  m1:6.1, m2:6.9, col:'white + white', note:'Even, tight pair for medium apertures.' },
    { id:'epsari',  name:'ε Arietis',                con:'Aries',         ra:2.9598,  dec:21.3380, sep:1.4,  m1:5.2, m2:5.5, col:'white + white', note:'Neat matched close pair.' },
    { id:'delcyg',  name:'δ Cygni',                  con:'Cygnus',        ra:19.7495, dec:45.1308, sep:2.7,  m1:2.9, m2:6.3, col:'blue-white', note:'Moderate glare pair; rewarding in good seeing.' }
  ];

  /* ----------------------------------------------------------------------
   * DATA — eyepieces stocked by Ontario Telescope. fl in mm ("zoom" flagged),
   * afov = apparent field in degrees, px = price CAD, st = in stock (1/0),
   * u = product URL, img = image. AFOV is the series-nominal figure.
   * -------------------------------------------------------------------- */
  var EYEPIECES = [{"b":"Celestron","s":"X-Cel LX","fl":2.3,"flMin":null,"flMax":null,"afov":60,"px":153.99,"st":1,"u":"/products/x-cel-lx-eyepiece-1-25-2-3mm","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/93420_X-Cel_LX_Eyepiece_1-25_2-3_mm_1_252520_25281_2529__31544.1682634879.1280.1280.jpg"},{"b":"Vixen","s":"SLV","fl":2.5,"flMin":null,"flMax":null,"afov":50,"px":237,"st":0,"u":"/products/vixen-slv-50-eyepiece-2-5mm-1-25","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/image_2022_11_29T17_25_18_056Z__32242.png"},{"b":"ES","s":"ES 52°","fl":3,"flMin":null,"flMax":null,"afov":52,"px":149,"st":0,"u":"/products/explore-scientific-3mm-52-series-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/EPWP5203-01_1_1663x__69129.jpg"},{"b":"Vixen","s":"SLV","fl":4,"flMin":null,"flMax":null,"afov":50,"px":237,"st":0,"u":"/products/vixen-slv-50-eyepiece-4mm-1-25","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/image_2022_11_29T20_08_02_496Z__79128.png"},{"b":"ES","s":"ES 82°","fl":4.5,"flMin":null,"flMax":null,"afov":82,"px":345,"st":0,"u":"/products/explore-scientific-4-5mm-82deg-series-ler-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/82_45_1800x1800__11504.jpg"},{"b":"ES","s":"ES 52°","fl":4.5,"flMin":null,"flMax":null,"afov":52,"px":149,"st":0,"u":"/products/explore-scientific-4-5mm-52-series-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/EPWP5245-01_3_1663x__19766.jpg"},{"b":"Baader","s":"Morpheus","fl":4.5,"flMin":null,"flMax":null,"afov":76,"px":404.99,"st":0,"u":"/products/4-5-mm-morpheus-76-widefield-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/45-mm-morpheus-76-widefield-eyepiece-546__88373.jpg"},{"b":"Celestron","s":"X-Cel LX","fl":5,"flMin":null,"flMax":null,"afov":60,"px":153.99,"st":0,"u":"/products/x-cel-lx-eyepiece-1-25-5mm","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/93421_X-Cel_LX_Eyepiece_1-25_5_mm_1__72881.jpg"},{"b":"Vixen","s":"SLV","fl":5,"flMin":null,"flMax":null,"afov":50,"px":237,"st":0,"u":"/products/vixen-slv-50-eyepiece-5mm-1-25","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/image_2022_11_29T19_23_25_652Z__22666.png"},{"b":"ES","s":"ES 100°","fl":5.5,"flMin":null,"flMax":null,"afov":100,"px":742,"st":0,"u":"/products/5-5mm-100-series-argon-purged-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/55_medium__50117.jpg"},{"b":"ES","s":"ES 62°","fl":5.5,"flMin":null,"flMax":null,"afov":62,"px":194,"st":1,"u":"/products/explore-scientific-62deg-5-5mm-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/62Degree_55_1__09707.jpg"},{"b":"Vixen","s":"SLV","fl":6,"flMin":null,"flMax":null,"afov":50,"px":237,"st":0,"u":"/products/vixen-slv-50-eyepiece-6mm-1-25","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/image_2022_11_29T19_40_26_322Z__82100.png"},{"b":"ES","s":"ES 82°","fl":6.5,"flMin":null,"flMax":null,"afov":82,"px":345,"st":0,"u":"/products/explore-scientific-6-5mm-82-deg-series-ler-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/82_65_1800x1800__15675.jpg"},{"b":"ES","s":"ES 52°","fl":6.5,"flMin":null,"flMax":null,"afov":52,"px":161.99,"st":0,"u":"/products/explore-scientific-52-6-5mm-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/EPWP5265-01_3_1663x__70299.jpg"},{"b":"Baader","s":"Morpheus","fl":6.5,"flMin":null,"flMax":null,"afov":76,"px":404.99,"st":0,"u":"/products/6-5-mm-morpheus-76-widefield-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/45-mm-morpheus-76-widefield-eyepiece-ef3__03625.jpg"},{"b":"Celestron","s":"Luminos","fl":7,"flMin":null,"flMax":null,"afov":82,"px":194,"st":0,"u":"/products/luminos-eyepiece-1-25-7mm","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/93430_Luminos_7mm_Eyepiece_1__45115.jpg"},{"b":"Celestron","s":"X-Cel LX","fl":7,"flMin":null,"flMax":null,"afov":60,"px":153.99,"st":0,"u":"/products/x-cel-lx-eyepiece-1-25-7mm","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/93422_X-Cel_LX_Eyepiece_1-25_7_mm_1__21475.jpg"},{"b":"PENTAX","s":"XW","fl":7,"flMin":null,"flMax":null,"afov":70,"px":449.99,"st":1,"u":"/products/eyepiece-smc-pentax-xw-7","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/xw7_693x__33313.jpg"},{"b":"ES","s":"ES 82°","fl":8.5,"flMin":null,"flMax":null,"afov":82,"px":345,"st":0,"u":"/products/explore-scientific-8-5mm-82-deg-series-ler-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/82_85_1800x1800__57079.jpg"},{"b":"ES","s":"ES 120°","fl":9,"flMin":null,"flMax":null,"afov":120,"px":1677,"st":0,"u":"/products/explore-scientific-120-9mm-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/1209MM__62924.jpg"},{"b":"ES","s":"ES 100°","fl":9,"flMin":null,"flMax":null,"afov":100,"px":783,"st":0,"u":"/products/9mm-100-series-argon-purged-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/9_medium__84515.jpg"},{"b":"ES","s":"ES 62°","fl":9,"flMin":null,"flMax":null,"afov":62,"px":215,"st":0,"u":"/products/explore-scientific-62deg-9mm-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/EPWP6220LE-01_Front___27033.jpg"},{"b":"Celestron","s":"X-Cel LX","fl":9,"flMin":null,"flMax":null,"afov":60,"px":153.99,"st":0,"u":"/products/x-cel-lx-eyepiece-1-25-9mm","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/93423_X-Cel_LX_Eyepiece_1-25_9_mm_1__93296.jpg"},{"b":"Baader","s":"Morpheus","fl":9,"flMin":null,"flMax":null,"afov":76,"px":404.99,"st":0,"u":"/products/9-mm-morpheus-76-widefield-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/9-mm-morpheus-76-widefield-eyepiece-264__07324.jpg"},{"b":"Vixen","s":"SLV","fl":9,"flMin":null,"flMax":null,"afov":50,"px":237,"st":0,"u":"/products/vixen-slv-50-eyepiece-9mm-1-25","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/image_2022_11_29T20_15_59_871Z__28150.png"},{"b":"ES","s":"ES 52°","fl":10,"flMin":null,"flMax":null,"afov":52,"px":161.99,"st":0,"u":"/products/explore-scientific-10mm-52-series-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/EPWP5210-01_1_1663x__81546.jpg"},{"b":"Celestron","s":"Luminos","fl":10,"flMin":null,"flMax":null,"afov":82,"px":194,"st":1,"u":"/products/luminos-eyepiece-1-25-10mm","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/93431_Luminos_10mm_Eyepiece_1__22208.jpg"},{"b":"Celestron","s":"Ultima Edge","fl":10,"flMin":null,"flMax":null,"afov":65,"px":152.99,"st":0,"u":"/products/ultima-edge-10mm-flat-field-eyepiece-1-25","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/93450_10mm_Ultima_Edge_Eyepiece_01_570x3803x__96810.jpg"},{"b":"Vixen","s":"SLV","fl":10,"flMin":null,"flMax":null,"afov":50,"px":237,"st":0,"u":"/products/vixen-slv-50-eyepiece-10mm-1-25","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/image_2022_11_29T19_52_25_535Z__40865.png"},{"b":"PENTAX","s":"XW","fl":10,"flMin":null,"flMax":null,"afov":70,"px":449.99,"st":1,"u":"/products/eyepiece-smc-pentax-xw-10","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/xw10_693x__17238.jpg"},{"b":"ES","s":"ES 92°","fl":12,"flMin":null,"flMax":null,"afov":92,"px":1032,"st":0,"u":"/products/12mm-92-eyepiece-2","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/12mm_25252092_2525202__28314.1693593625.1280.1280.jpg"},{"b":"Celestron","s":"X-Cel LX","fl":12,"flMin":null,"flMax":null,"afov":60,"px":153.99,"st":0,"u":"/products/x-cel-lx-eyepiece-1-25-12mm","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/93424_252520X-Cel_252520LX__06349.1682634881.1280.1280.jpg"},{"b":"Vixen","s":"SLV","fl":12,"flMin":null,"flMax":null,"afov":50,"px":237,"st":0,"u":"/products/vixen-slv-50-eyepiece-12mm-1-25","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/image_2022_11_29T20_30_34_071Z__83582.png"},{"b":"Baader","s":"Morpheus","fl":12.5,"flMin":null,"flMax":null,"afov":76,"px":404.99,"st":0,"u":"/products/12-5-mm-morpheus-76-widefield-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/125-mm-morpheus-76-widefield-eyepiece-05f__38043.jpg"},{"b":"ES","s":"ES 100°","fl":14,"flMin":null,"flMax":null,"afov":100,"px":877,"st":0,"u":"/products/14mm-100-series-argon-purged-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/14_medium__57014.jpg"},{"b":"ES","s":"ES 62°","fl":14,"flMin":null,"flMax":null,"afov":62,"px":215,"st":0,"u":"/products/explore-scientific-62deg-14mm-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/EPWP6214LE-01_Front__18484.jpg"},{"b":"Baader","s":"Morpheus","fl":14,"flMin":null,"flMax":null,"afov":76,"px":404.99,"st":0,"u":"/products/14-mm-morpheus-76-widefield-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/14-mm-morpheus-76-widefield-eyepiece-74e__78302.jpg"},{"b":"PENTAX","s":"XW","fl":14,"flMin":null,"flMax":null,"afov":70,"px":449.99,"st":0,"u":"/products/eyepiece-smc-pentax-xw-14","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/xw14_693x__61650.jpg"},{"b":"ES","s":"ES 52°","fl":15,"flMin":null,"flMax":null,"afov":52,"px":161.99,"st":0,"u":"/products/explore-scientific-52mm-15mm-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/EPWP5215-01_3_1663x__48809.jpg"},{"b":"Celestron","s":"Luminos","fl":15,"flMin":null,"flMax":null,"afov":82,"px":194,"st":1,"u":"/products/luminos-eyepiece-1-25-15mm","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/93432_Luminos_15mm_Eyepiece_1__22630.jpg"},{"b":"Celestron","s":"Ultima Edge","fl":15,"flMin":null,"flMax":null,"afov":65,"px":222.99,"st":1,"u":"/products/ultima-edge-15mm-flat-field-eyepiece-1-25","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/93450_10mm_Ultima_Edge_Eyepiece_01_570x3803x__32267.jpg"},{"b":"Vixen","s":"SLV","fl":15,"flMin":null,"flMax":null,"afov":50,"px":237,"st":0,"u":"/products/vixen-slv-50-eyepiece-15mm-1-25","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/image_2022_11_29t20_46_26_629z__62331.png"},{"b":"ES","s":"ES 68°","fl":16,"flMin":null,"flMax":null,"afov":68,"px":319.99,"st":1,"u":"/products/16mm-68-deg-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/16_1024x1024__61719.jpg"},{"b":"PENTAX","s":"XW","fl":16.5,"flMin":null,"flMax":null,"afov":70,"px":719.99,"st":0,"u":"/products/eyepiece-smc-pentax-xw-16-5","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/01XW16.5_693x__90458.jpg"},{"b":"ES","s":"ES 92°","fl":17,"flMin":null,"flMax":null,"afov":92,"px":1032,"st":0,"u":"/products/17mm-92-eyepiece-2","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/17mm_25252092_2525202__78293.1693593626.1280.1280.jpg"},{"b":"Baader","s":"Morpheus","fl":17.5,"flMin":null,"flMax":null,"afov":76,"px":404.99,"st":1,"u":"/products/17-5-mm-morpheus-76-widefield-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/175-mm-morpheus-76-widefield-eyepiece-c7f__27205.jpg"},{"b":"Celestron","s":"X-Cel LX","fl":18,"flMin":null,"flMax":null,"afov":60,"px":153.99,"st":1,"u":"/products/x-cel-lx-eyepiece-1-25-18mm","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/93425-X-Cel-LX__85929.jpg"},{"b":"Celestron","s":"Ultima Edge","fl":18,"flMin":null,"flMax":null,"afov":65,"px":236.99,"st":1,"u":"/products/ultima-edge-18mm-flat-field-eyepiece-1-25","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/93452_18mm_Ultima_Edge_Eyepiece_01_570x3802x__69215.jpg"},{"b":"Celestron","s":"Luminos","fl":19,"flMin":null,"flMax":null,"afov":82,"px":277,"st":1,"u":"/products/luminos-eyepiece-2-19mm","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/93433_Luminos_19mm_Eyepiece_1__80429.jpg"},{"b":"ES","s":"ES 100°","fl":20,"flMin":null,"flMax":null,"afov":100,"px":1080,"st":0,"u":"/products/20mm-100-series-argon-purged-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/20_medium__63241.jpg"},{"b":"ES","s":"ES 68°","fl":20,"flMin":null,"flMax":null,"afov":68,"px":297,"st":0,"u":"/products/20mm-68-deg-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/20_1024x1024__94807.jpg"},{"b":"ES","s":"ES 62°","fl":20,"flMin":null,"flMax":null,"afov":62,"px":142,"st":0,"u":"/products/explore-scientific-62-20mm-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/EPWP6220LE-01_Front__26872.jpg"},{"b":"ES","s":"ES 52°","fl":20,"flMin":null,"flMax":null,"afov":52,"px":161.99,"st":0,"u":"/products/explore-scientific-20mm-52-series-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/EPWP5220-01_3_1663x__95165.jpg"},{"b":"Vixen","s":"SLV","fl":20,"flMin":null,"flMax":null,"afov":50,"px":237,"st":0,"u":"/products/vixen-slv-50-eyepiece-20mm-1-25","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/image_2022_11_29T20_53_28_315Z__87125.png"},{"b":"Antares","s":"XWA","fl":20,"flMin":null,"flMax":null,"afov":100,"px":299.99,"st":0,"u":"/products/antares-2-20mm-xwa-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/2-20mm-xwa-eyepiece__51300.jpg"},{"b":"PENTAX","s":"XW","fl":20,"flMin":null,"flMax":null,"afov":70,"px":449.99,"st":0,"u":"/products/eyepiece-smc-pentax-xw-20","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/xw20_693x__35577.jpg"},{"b":"Celestron","s":"Luminos","fl":23,"flMin":null,"flMax":null,"afov":82,"px":347,"st":1,"u":"/products/luminos-eyepiece-2-23mm","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/93433_Luminos_19mm_Eyepiece_1__53638.jpg"},{"b":"PENTAX","s":"XW","fl":23,"flMin":null,"flMax":null,"afov":70,"px":749.99,"st":0,"u":"/products/eyepiece-smc-pentax-xw-23","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/01XW23_693x__83492.jpg"},{"b":"ES","s":"ES 68°","fl":24,"flMin":null,"flMax":null,"afov":68,"px":364.99,"st":0,"u":"/products/24mm-68-deg-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/esepwp682401__39958.jpg"},{"b":"Celestron","s":"Ultima Edge","fl":24,"flMin":null,"flMax":null,"afov":65,"px":361.99,"st":1,"u":"/products/ultima-edge-24mm-flat-field-eyepiece-1-25","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/93453_24mm_Ultima_Edge_Eyepiece_01_570x3802x__54207.jpg"},{"b":"ES","s":"ES 100°","fl":25,"flMin":null,"flMax":null,"afov":100,"px":1350,"st":0,"u":"/products/25mm-100-series-argon-purged-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/25_medium__39410.jpg"},{"b":"ES","s":"ES 52°","fl":25,"flMin":null,"flMax":null,"afov":52,"px":161.99,"st":1,"u":"/products/explore-scientific-25mm-52-series-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/EPWP5220-01_3_1663x__26580.jpg"},{"b":"Celestron","s":"X-Cel LX","fl":25,"flMin":null,"flMax":null,"afov":60,"px":153.99,"st":0,"u":"/products/x-cel-lx-eyepiece-1-25-25mm","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/93426_xcel_lx_25mm_eyepiece-drc__80459.jpg"},{"b":"Vixen","s":"SLV","fl":25,"flMin":null,"flMax":null,"afov":50,"px":237,"st":0,"u":"/products/vixen-slv-50-eyepiece-25mm-1-25","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/image_2022_11_29T21_11_47_109Z__71685.png"},{"b":"ES","s":"ES 62°","fl":26,"flMin":null,"flMax":null,"afov":62,"px":232,"st":0,"u":"/products/explore-scientific-62deg-26mm-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/EPWP6226LE-01_Front__98248.jpg"},{"b":"ES","s":"ES 68°","fl":28,"flMin":null,"flMax":null,"afov":68,"px":434.99,"st":0,"u":"/products/28mm-68-deg-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/2104280-2__00802.jpg"},{"b":"ES","s":"ES 100°","fl":30,"flMin":null,"flMax":null,"afov":100,"px":1485,"st":0,"u":"/products/30mm-100-series-argon-purged-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/30_medium__48363.jpg"},{"b":"ES","s":"ES 52°","fl":30,"flMin":null,"flMax":null,"afov":52,"px":202.49,"st":0,"u":"/products/explore-scientific-30mm-52-series-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/EPWP5230-01_1_1663x__25015.jpg"},{"b":"Celestron","s":"Ultima Edge","fl":30,"flMin":null,"flMax":null,"afov":65,"px":416.99,"st":0,"u":"/products/ultima-edge-30mm-flat-field-eyepiece-2","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/93454_30mm_Ultima_Edge_Eyepiece_01_570x3802x__02965.jpg"},{"b":"Celestron","s":"Luminos","fl":31,"flMin":null,"flMax":null,"afov":82,"px":416,"st":0,"u":"/products/luminos-eyepiece-2-31mm","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/93433_Luminos_19mm_Eyepiece_1__25271.jpg"},{"b":"ES","s":"ES 62°","fl":32,"flMin":null,"flMax":null,"afov":62,"px":330,"st":1,"u":"/products/explore-scientific-62deg-32mm-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/EPWP6232LE-01_Front__65971.jpg"},{"b":"ES","s":"ES 68°","fl":34,"flMin":null,"flMax":null,"afov":68,"px":604.99,"st":0,"u":"/products/34mm-68-deg-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/34_1024x1024__72695.jpg"},{"b":"ES","s":"ES 68°","fl":40,"flMin":null,"flMax":null,"afov":68,"px":671,"st":0,"u":"/products/40mm-68-deg-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/40mm__83807.jpg"},{"b":"ES","s":"ES 62°","fl":40,"flMin":null,"flMax":null,"afov":62,"px":374,"st":0,"u":"/products/explore-scientific-62-40mm-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/62Degree_40mm_EPWP6240LE-01__96574.jpg"},{"b":"ES","s":"ES 52°","fl":40,"flMin":null,"flMax":null,"afov":52,"px":242.99,"st":0,"u":"/products/explore-scientific-40mm-52-series-waterproof-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/EPWP5240-01_3_1663x__68774.jpg"},{"b":"Celestron","s":"Zoom","fl":"zoom","flMin":8,"flMax":24,"afov":50,"px":150,"st":1,"u":"/products/1-25-8-24mm-zoom-eyepiece","img":"https://cdn.shopify.com/s/files/1/0686/0049/6239/files/93230-zoom-eyepiece-8-24-mm_02__06671.jpg"}];

  /* ======================================================================
   * PURE MATH  (no DOM here — unit-tested in Node)
   * ==================================================================== */
  var M = {};

  var DEG = Math.PI / 180;
  function clamp(x, lo, hi) { return Math.max(lo, Math.min(hi, x)); }
  function mod(a, n) { return ((a % n) + n) % n; }
  M.clamp = clamp;

  // Theoretical limits — D in mm, result in arcseconds.
  M.dawes    = function (D) { return 116.0 / D; };          // 4.56"/inch
  M.rayleigh = function (D, lambdaNm) {                       // 1.22 λ / D
    var lam = (lambdaNm || 550) * 1e-6;                       // nm -> mm
    return 1.22 * lam / D * 206265;
  };
  M.sparrow  = function (D) { return 107.0 / D; };           // ~0.92 x Dawes

  // Optics geometry
  M.magnification = function (scopeFL, epFL) { return scopeFL / epFL; };
  M.tfov          = function (afovDeg, mag) { return afovDeg / mag; };        // degrees
  M.exitPupil     = function (apMm, mag) { return apMm / mag; };              // mm
  M.maxUsefulMag  = function (apMm) { return 2.0 * apMm; };                    // ~0.5 mm exit pupil

  /* --------------------------------------------------------------------
   * The real-world resolving model.
   * Inputs (object):
   *   ap    aperture (mm)
   *   fl    focal length (mm)          [optional, for mag suggestions]
   *   obs   central obstruction (fraction, 0..~0.4)
   *   seeing atmospheric seeing FWHM (arcsec)
   *   sep   pair separation (arcsec)
   *   m1,m2 component magnitudes
   *   color colour-difference relief, 0 (none) .. 1 (strong, e.g. gold/blue)
   *   exp   observer experience, 0 (beginner) .. 1 (expert)
   *   alt   target altitude in degrees [optional; worsens effective seeing]
   * Returns numbers + a verdict key + which factor limits the split.
   * ------------------------------------------------------------------ */
  M.resolve = function (p) {
    var D = p.ap;
    var dawes = M.dawes(D);
    var rayleigh = M.rayleigh(D);
    var dm = Math.abs(p.m2 - p.m1);
    var primary = Math.min(p.m1, p.m2);
    var obs = clamp(p.obs || 0, 0, 0.6);
    var exp = clamp(p.exp == null ? 0.5 : p.exp, 0, 1);
    var color = clamp(p.color || 0, 0, 1);

    // Effective seeing, degraded by low altitude (airmass).
    var seeing = Math.max(0.3, p.seeing || 2.0);
    var airmass = 1;
    if (p.alt != null && p.alt > 0) {
      airmass = 1 / Math.sin(clamp(p.alt, 1, 90) * DEG);
      seeing = seeing * Math.pow(airmass, 0.6);
    }

    // Diffraction term — experts push slightly below Dawes, beginners above.
    var diffLimit = dawes * (1.15 - 0.30 * exp);

    // Glare term — the companion must clear the primary's scattered-light
    // aureole. The radius grows with the brightness ratio (a softened power
    // of 10^(0.4*dm)), a brighter primary, a larger obstruction and worse
    // seeing. Calibrated so Sirius B (dm~10) reads "touching / very hard"
    // in excellent conditions and "elongated / single" in poor ones.
    var g0 = 0.115 * Math.pow(10, 0.18 * dm);
    var primaryFactor = Math.pow(10, 0.04 * (2.0 - primary));   // brighter primary => bigger halo
    primaryFactor = clamp(primaryFactor, 0.75, 1.6);
    var obsFactor = 1 + 0.9 * obs;
    var seeingGlare = 1 + 0.15 * Math.max(0, seeing - 1);
    var glare = g0 * primaryFactor * obsFactor * seeingGlare;

    // Colour contrast makes an unequal/tight pair easier to tell apart.
    var colorRelief = 1 - 0.12 * color;

    // Separation the optics+glare demand, and the atmospheric floor.
    var requiredSep = Math.sqrt(diffLimit * diffLimit + glare * glare) * colorRelief;
    var seeingFloor = seeing * (1.10 - 0.45 * exp);
    var effectiveRes = Math.max(requiredSep, seeingFloor);

    var ratio = p.sep / effectiveRes;

    var verdict;
    if (ratio >= 1.5)       verdict = 'clean';
    else if (ratio >= 1.1)  verdict = 'split';
    else if (ratio >= 0.85) verdict = 'touching';
    else if (ratio >= 0.6)  verdict = 'elongated';
    else                    verdict = 'single';

    // Which factor is the wall?
    var limiter;
    if (seeingFloor > requiredSep * 1.05) limiter = 'seeing';
    else if (glare > diffLimit)           limiter = 'magnitude';
    else                                  limiter = 'aperture';

    // Suggested magnification to present the pair comfortably to the eye
    // (~200" apparent separation), floored for tight pairs, capped by optics.
    var recMag = 240 / p.sep;
    recMag = clamp(recMag, 30, M.maxUsefulMag(D));
    recMag = Math.round(recMag / 5) * 5;

    return {
      dawes: dawes,
      rayleigh: rayleigh,
      sparrow: M.sparrow(D),
      dm: dm,
      primary: primary,
      seeingEff: seeing,
      airmass: airmass,
      diffLimit: diffLimit,
      glare: glare,
      requiredSep: requiredSep,
      seeingFloor: seeingFloor,
      effectiveRes: effectiveRes,
      ratio: ratio,
      verdict: verdict,
      limiter: limiter,
      withinAperture: p.sep >= dawes,
      withinSeeing: p.sep >= seeingFloor,
      recMag: recMag
    };
  };

  /* ======================================================================
   * EPHEMERIS  (pure — unit-tested in Node)
   * ==================================================================== */
  var E = {};

  E.julianDay = function (date) { return date.getTime() / 86400000 + 2440587.5; };

  E.gmstDeg = function (jd) {
    var d = jd - 2451545.0;
    var T = d / 36525.0;
    var g = 280.46061837 + 360.98564736629 * d + 0.000387933 * T * T - (T * T * T) / 38710000.0;
    return mod(g, 360);
  };

  // Altitude/azimuth of an object. raHours in hours, decDeg/latDeg/lonDeg in
  // degrees (east-positive longitude). Returns {alt,az} in degrees.
  E.altAz = function (raHours, decDeg, latDeg, lonDeg, date) {
    var ra = raHours * 15.0;
    var jd = E.julianDay(date);
    var lst = mod(E.gmstDeg(jd) + lonDeg, 360);
    var ha = mod(lst - ra + 180, 360) - 180;         // -180..180
    var haR = ha * DEG, decR = decDeg * DEG, latR = latDeg * DEG;
    var sinAlt = Math.sin(latR) * Math.sin(decR) + Math.cos(latR) * Math.cos(decR) * Math.cos(haR);
    var alt = Math.asin(clamp(sinAlt, -1, 1)) / DEG;
    var az = Math.atan2(-Math.sin(haR),
      Math.tan(decR) * Math.cos(latR) - Math.sin(latR) * Math.cos(haR)) / DEG;
    az = mod(az, 360);
    return { alt: alt, az: az, ha: ha };
  };

  // Next upper-transit time and the altitude it reaches. Returns
  // {transit:Date, maxAlt:deg, everUp:bool, circumpolar:bool}.
  E.transit = function (raHours, decDeg, latDeg, lonDeg, date) {
    var ra = raHours * 15.0;
    var jd = E.julianDay(date);
    var lstNow = mod(E.gmstDeg(jd) + lonDeg, 360);
    var deltaDeg = mod(ra - lstNow, 360);            // degrees of sidereal rotation until meridian
    var hours = deltaDeg / 15.0410686;               // sidereal rate
    var transit = new Date(date.getTime() + hours * 3600 * 1000);
    var maxAlt = 90 - Math.abs(latDeg - decDeg);
    if (maxAlt > 90) maxAlt = 180 - maxAlt;
    var minAlt = Math.abs(latDeg + decDeg) - 90;     // lower-culmination altitude sign test
    var circumpolar = (latDeg >= 0 ? decDeg >= (90 - latDeg) : decDeg <= (-90 - latDeg));
    return { transit: transit, maxAlt: maxAlt, everUp: maxAlt > 0, circumpolar: circumpolar };
  };

  /* ======================================================================
   * Expose for Node unit tests / and stash on window for the UI layer.
   * ==================================================================== */
  var API = { TELESCOPES: TELESCOPES, DOUBLES: DOUBLES, EYEPIECES: EYEPIECES, M: M, E: E, clamp: clamp, mod: mod, DEG: DEG };
  if (typeof module !== 'undefined' && module.exports) { module.exports = API; }
  if (typeof window !== 'undefined') { window.DSC = API; }

  /* ======================================================================
   * DOM / UI
   * ==================================================================== */
  if (typeof document === 'undefined') return;

  // A handful of showpiece deep-sky objects for framing in the view tab.
  // ra hours, dec deg (J2000), size = largest angular size in arcminutes.
  var DSOS = [
    { id:'m42', name:'M42 — Orion Nebula',        ra:5.5881,  dec:-5.3911,  size:85 },
    { id:'m45', name:'M45 — Pleiades',            ra:3.7910,  dec:24.1050,  size:110 },
    { id:'m31', name:'M31 — Andromeda Galaxy',    ra:0.7123,  dec:41.2687,  size:190 },
    { id:'m13', name:'M13 — Hercules Cluster',    ra:16.6949, dec:36.4613,  size:20 },
    { id:'m57', name:'M57 — Ring Nebula',         ra:18.8933, dec:33.0287,  size:3.8 },
    { id:'m27', name:'M27 — Dumbbell Nebula',     ra:19.9934, dec:22.7212,  size:8 },
    { id:'m51', name:'M51 — Whirlpool Galaxy',    ra:13.4979, dec:47.1953,  size:11 },
    { id:'m81', name:'M81 — Bode’s Galaxy',  ra:9.9260,  dec:69.0653,  size:27 },
    { id:'m44', name:'M44 — Beehive Cluster',     ra:8.6700,  dec:19.6700,  size:95 },
    { id:'m11', name:'M11 — Wild Duck Cluster',   ra:18.8511, dec:-6.2711,  size:14 },
    { id:'ngc869', name:'NGC 869/884 — Double Cluster', ra:2.3319, dec:57.1367, size:60 }
  ];

  var $ = function (id) { return document.getElementById(id); };
  function h(html) { var t = document.createElement('template'); t.innerHTML = html.trim(); return t.content.firstChild; }
  function num(id) { var v = parseFloat(($(id) || {}).value); return isNaN(v) ? null : v; }
  function fmt(x, d) { if (x == null || isNaN(x)) return '—'; return (Math.round(x * Math.pow(10, d)) / Math.pow(10, d)).toFixed(d); }

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  ready(function () {
    var root = document.querySelector('.dsc');
    if (!root) return;

    /* ---- populate selects ------------------------------------------- */
    var scopeSel = $('dsc-scope');
    TELESCOPES.forEach(function (t) { scopeSel.appendChild(h('<option value="' + t.id + '">' + t.label + '</option>')); });
    scopeSel.value = 'nt200';

    var tgtSel = $('dsc-target');
    tgtSel.appendChild(h('<option value="manual">Manual entry — type separation &amp; magnitudes</option>'));
    var og = h('<optgroup label="Showpiece &amp; test doubles"></optgroup>');
    DOUBLES.forEach(function (d) { og.appendChild(h('<option value="' + d.id + '">' + d.name + ' — ' + fmt(d.sep,1) + '&Prime;, Δm ' + fmt(Math.abs(d.m2-d.m1),1) + '</option>')); });
    tgtSel.appendChild(og);

    var epSel = $('dsc-ep');
    epSel.appendChild(h('<option value="manual">Manual entry — type focal length &amp; field</option>'));
    var byBrand = {};
    EYEPIECES.forEach(function (e, i) { (byBrand[e.b] = byBrand[e.b] || []).push([e, i]); });
    Object.keys(byBrand).forEach(function (b) {
      var g = h('<optgroup label="' + b + '"></optgroup>');
      byBrand[b].forEach(function (pair) {
        var e = pair[0], i = pair[1];
        var flTxt = e.fl === 'zoom' ? (e.flMin + '–' + e.flMax + ' mm zoom') : (e.fl + ' mm');
        g.appendChild(h('<option value="' + i + '">' + e.s + ' ' + flTxt + ' · ' + e.afov + '°' + (e.st ? '' : ' (backorder)') + '</option>'));
      });
      epSel.appendChild(g);
    });

    var frSel = $('dsc-frame');
    var ogd = h('<optgroup label="Deep-sky objects"></optgroup>');
    DSOS.forEach(function (d) { ogd.appendChild(h('<option value="dso:' + d.id + '">' + d.name + '</option>')); });
    frSel.appendChild(ogd);
    var ogp = h('<optgroup label="Double stars"></optgroup>');
    DOUBLES.forEach(function (d) { ogp.appendChild(h('<option value="dbl:' + d.id + '">' + d.name + '</option>')); });
    frSel.appendChild(ogp);

    /* ---- scope preset behaviour ------------------------------------- */
    function applyScope() {
      var t = TELESCOPES.filter(function (x) { return x.id === scopeSel.value; })[0];
      if (!t || t.id === 'manual') return;
      if (t.ap) $('dsc-ap').value = t.ap;
      if (t.fl) $('dsc-fl').value = t.fl;
      $('dsc-obs').value = t.obs;
    }
    scopeSel.addEventListener('change', applyScope);
    applyScope();

    function readScope() {
      return { ap: num('dsc-ap'), fl: num('dsc-fl'), obs: (num('dsc-obs') || 0) };
    }

    /* ---- target preset behaviour ------------------------------------ */
    function colorToVal(col) {
      col = (col || '').toLowerCase();
      var parts = col.split('+').map(function (s) { return s.trim(); });
      if (parts.length < 2) return 0.1;
      if (parts[0] === parts[1]) return 0.1;
      // strong contrasts
      if (/gold|red|orange|yellow/.test(parts[0]) && /blue|green|white/.test(parts[1])) return 0.85;
      if (/blue|green/.test(parts[0]) && /gold|red|orange|yellow/.test(parts[1])) return 0.85;
      return 0.4;
    }
    function applyTarget() {
      var d = DOUBLES.filter(function (x) { return x.id === tgtSel.value; })[0];
      var manual = !d;
      ['dsc-sep', 'dsc-m1', 'dsc-m2'].forEach(function (id) { $(id).readOnly = !manual; });
      $('dsc-color').disabled = !manual;
      if (d) {
        $('dsc-sep').value = d.sep; $('dsc-m1').value = d.m1; $('dsc-m2').value = d.m2;
        $('dsc-color').value = String(colorToVal(d.col));
        $('dsc-tgt-note').textContent = d.con + ' · ' + d.col + ' · ' + d.note;
        root.setAttribute('data-radec', d.ra + ',' + d.dec);
      } else {
        $('dsc-tgt-note').textContent = 'Enter the pair’s separation and both magnitudes.';
        root.removeAttribute('data-radec');
      }
    }
    tgtSel.addEventListener('change', applyTarget);
    applyTarget();

    /* ---- geolocation ------------------------------------------------ */
    $('dsc-geo').addEventListener('click', function () {
      if (!navigator.geolocation) { $('dsc-geo-note').textContent = 'Geolocation not available — type your coordinates.'; return; }
      $('dsc-geo-note').textContent = 'Locating…';
      navigator.geolocation.getCurrentPosition(function (pos) {
        $('dsc-lat').value = fmt(pos.coords.latitude, 4);
        $('dsc-lon').value = fmt(pos.coords.longitude, 4);
        $('dsc-geo-note').textContent = 'Location set.';
      }, function () { $('dsc-geo-note').textContent = 'Couldn’t get location — type your coordinates.'; });
    });

    /* ================================================================
     * SYNTHETIC SPLIT RENDER — draws the pair as the eyepiece shows it:
     * two seeing-blurred stars with brightness ratio, primary glare and
     * (for obstructed scopes) a faint diffraction ring.
     * ============================================================== */
    function drawSplit(canvas, p, res) {
      var ctx = canvas.getContext('2d');
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var W = canvas.clientWidth || 340, Hh = W; // square
      canvas.width = W * dpr; canvas.height = Hh * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = '#000'; ctx.fillRect(0, 0, W, Hh);

      var sep = p.sep;
      // Optical on-sky blur (never smaller than the diffraction disk), then
      // fold in the eye's OWN resolving limit at this magnification, so the
      // view responds to the eyepiece: too little power and the pair merges,
      // more power pulls it apart until seeing/optics set the floor.
      var opticalFwhm = Math.max(res.seeingEff, res.dawes * 1.0);
      var eyeOnSky = p.mag ? (3.0 * 60 / p.mag) : 0;   // ~3' comfortable eye acuity, arcsec on sky
      var fwhm = Math.sqrt(opticalFwhm * opticalFwhm + eyeOnSky * eyeOnSky);
      // field of view shown across the canvas (arcsec) — keep the pair visible
      var field = clamp(Math.max(sep * 6, fwhm * 6), sep * 4, sep * 30);
      if (field < 4) field = 4;
      var pxa = W / field;
      var cx = W / 2, cy = Hh / 2;
      var half = sep * pxa / 2;
      var sigma = Math.max(1.2, (fwhm / 2.355) * pxa);

      var dm = res.dm;
      var compPeak = clamp(Math.pow(10, -0.4 * dm), 0.05, 1);

      ctx.globalCompositeOperation = 'lighter';

      // primary glare aureole (this is what buries the companion)
      var glarePx = Math.max(sigma * 2, res.glare * pxa);
      var gr = ctx.createRadialGradient(cx - half, cy, 0, cx - half, cy, glarePx);
      gr.addColorStop(0, 'rgba(255,250,235,0.55)');
      gr.addColorStop(0.25, 'rgba(255,248,230,0.16)');
      gr.addColorStop(1, 'rgba(255,248,230,0)');
      ctx.fillStyle = gr; ctx.beginPath(); ctx.arc(cx - half, cy, glarePx, 0, 7); ctx.fill();

      function star(x, y, peak, tint) {
        var r = sigma * 3.2;
        var g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, 'rgba(' + tint + ',' + peak + ')');
        g.addColorStop(0.35, 'rgba(' + tint + ',' + (peak * 0.35) + ')');
        g.addColorStop(1, 'rgba(' + tint + ',0)');
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, r, 0, 7); ctx.fill();
        // bright core
        var c = ctx.createRadialGradient(x, y, 0, x, y, sigma);
        c.addColorStop(0, 'rgba(255,255,255,' + Math.min(1, peak + 0.2) + ')');
        c.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = c; ctx.beginPath(); ctx.arc(x, y, sigma, 0, 7); ctx.fill();
      }

      // faint diffraction ring for obstructed optics (illustrative)
      if (p.obs > 0.15) {
        ctx.globalCompositeOperation = 'lighter';
        ctx.strokeStyle = 'rgba(255,250,235,' + (0.10 + 0.25 * p.obs) + ')';
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(cx - half, cy, Math.max(sigma * 1.6, res.dawes * pxa * 1.2), 0, 7); ctx.stroke();
      }

      var primaryTint = '255,250,240';
      var compTint = '210,225,255';
      star(cx - half, cy, 1.0, primaryTint);   // primary
      star(cx + half, cy, compPeak, compTint);  // companion

      // scale bar
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = 'rgba(255,255,255,0.6)'; ctx.fillStyle = 'rgba(255,255,255,0.75)';
      ctx.font = '11px ui-monospace, monospace';
      var barArc = niceBar(field / 4);
      var barPx = barArc * pxa;
      var bx = 14, by = Hh - 16;
      ctx.beginPath(); ctx.moveTo(bx, by); ctx.lineTo(bx + barPx, by); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(bx, by - 3); ctx.lineTo(bx, by + 3); ctx.moveTo(bx + barPx, by - 3); ctx.lineTo(bx + barPx, by + 3); ctx.stroke();
      ctx.fillText(barArc + '″', bx, by - 6);
      ctx.textAlign = 'right';
      ctx.fillText('sep ' + fmt(sep, 1) + '″ · seeing ' + fmt(res.seeingEff, 1) + '″', W - 12, by - 6);
      ctx.textAlign = 'left';
    }
    function niceBar(x) {
      var pow = Math.pow(10, Math.floor(Math.log(x) / Math.LN10));
      var n = x / pow;
      var m = n >= 5 ? 5 : n >= 2 ? 2 : 1;
      return m * pow;
    }

    /* ----------------------------------------------------------------
     * Eyepiece-driven simulated view: pick an eyepiece (grouped by the
     * manufacturers we stock) or type a focal length, and re-render the
     * split at that magnification. Uses the shared telescope inputs.
     * -------------------------------------------------------------- */
    var simCtx = null;
    function fillSimEyepieces(sel, recIdx) {
      sel.innerHTML = '';
      var groups = {};
      EYEPIECES.forEach(function (e, i) { (groups[e.b] = groups[e.b] || []).push([e, i]); });
      Object.keys(groups).forEach(function (b) {
        var g = h('<optgroup label="' + b + '"></optgroup>');
        groups[b].forEach(function (pair) {
          var e = pair[0], i = pair[1];
          var flTxt = e.fl === 'zoom' ? (e.flMin + '–' + e.flMax + ' mm zoom') : (e.fl + ' mm');
          var lbl = e.s + ' ' + flTxt + ' · ' + e.afov + '°' + (e.st ? '' : ' (backorder)') + (i === recIdx ? ' — recommended' : '');
          g.appendChild(h('<option value="' + i + '">' + lbl + '</option>'));
        });
        sel.appendChild(g);
      });
    }
    function ensureSimControls() {
      if ($('dsc-sim-controls')) return;
      var view = document.querySelector('#dsc-results .dsc-view');
      if (!view) return;
      var host = view.children[1] || view;
      var block = h('<div class="dsc-group" id="dsc-sim-controls" style="margin-top:1.1rem">' +
        '<h3>See it through an eyepiece</h3>' +
        '<div class="dsc-grid" style="grid-template-columns:1fr 1fr">' +
        '<div class="dsc-field"><label>Eyepiece</label><select id="dsc-sim-ep"></select><p class="dsc-sub">Grouped by the makers we stock.</p></div>' +
        '<div class="dsc-field"><label>…or eyepiece focal length <span class="dsc-u">mm</span></label><input type="number" id="dsc-sim-fl" min="1" step="0.5" placeholder="e.g. 6"><p class="dsc-sub">Overrides the dropdown.</p></div>' +
        '</div>' +
        '<div class="dsc-kpis" id="dsc-sim-readout" style="grid-template-columns:repeat(3,minmax(0,1fr));margin-top:.7rem"></div>' +
        '</div>');
      host.appendChild(block);
      $('dsc-sim-ep').addEventListener('change', function () { $('dsc-sim-fl').value = ''; renderSim(); });
      $('dsc-sim-fl').addEventListener('input', renderSim);
    }
    function currentSimMag() {
      var sc = simCtx.sc, res = simCtx.res;
      var flManual = num('dsc-sim-fl');
      if (flManual && flManual > 0 && sc.fl) return { mag: sc.fl / flManual, label: 'your ' + fmt(flManual, 1) + ' mm eyepiece', manualFl: flManual };
      var sel = $('dsc-sim-ep');
      var idx = sel ? parseInt(sel.value, 10) : NaN;
      if (isNaN(idx) || !EYEPIECES[idx]) return { mag: res.recMag, label: 'the recommended magnification' };
      var e = EYEPIECES[idx];
      var epfl = e.fl === 'zoom' ? Math.round((e.flMin + e.flMax) / 2) : e.fl;
      if (!sc.fl) return { mag: res.recMag, label: 'the recommended magnification', e: e };
      return { mag: sc.fl / epfl, label: e.b + ' ' + e.s + ' ' + epfl + ' mm', epfl: epfl, e: e };
    }
    function nearestEyepieceByFl(fl) {
      var best = null, be = 1e9;
      EYEPIECES.forEach(function (e) {
        var ff = e.fl === 'zoom' ? (e.flMin + e.flMax) / 2 : e.fl;
        var err = Math.abs(ff - fl) + (e.st ? 0 : 3);
        if (err < be) { be = err; best = e; }
      });
      return best;
    }
    function updateEyepieceCard(info) {
      var er = $('dsc-eprec'); if (!er) return;
      var sc = simCtx.sc;
      var e = info.manualFl ? nearestEyepieceByFl(info.manualFl) : info.e;
      if (e && sc.fl) {
        var note = info.manualFl
          ? 'Closest eyepiece we stock to your ' + fmt(info.manualFl, 1) + ' mm:'
          : 'Selected eyepiece in your ' + sc.ap + ' mm scope:';
        er.style.display = '';
        er.innerHTML = '<p class="dsc-sec-note">' + note + '</p>' + epCard(e, sc);
      } else { er.style.display = 'none'; er.innerHTML = ''; }
    }
    function renderSim() {
      if (!simCtx) return;
      var sc = simCtx.sc, res = simCtx.res;
      var info = currentSimMag();
      var mag = info.mag;
      drawSplit($('dsc-canvas'), { sep: simCtx.sep, obs: sc.obs, mag: mag }, res);
      updateEyepieceCard(info);
      var exit = sc.ap ? M.exitPupil(sc.ap, mag) : null;
      var maxU = sc.ap ? M.maxUsefulMag(sc.ap) : null;
      var eyeOnSky = 3.0 * 60 / mag;
      var opt = Math.max(res.seeingEff, res.dawes);
      var ratioAtM = simCtx.sep / Math.sqrt(opt * opt + eyeOnSky * eyeOnSky);
      var judged = ratioAtM >= 1.3 ? 'splits well' : ratioAtM >= 1.0 ? 'just splits' : ratioAtM >= 0.8 ? 'elongated' : (mag < res.recMag * 0.7 ? 'too low — merges' : 'not at this seeing');
      var ro = $('dsc-sim-readout'); ro.innerHTML = '';
      function kpi(l, v, d) { ro.appendChild(h('<div class="dsc-kpi"><div class="dsc-l">' + l + '</div><div class="dsc-v">' + v + '</div><div class="dsc-d">' + (d || '') + '</div></div>')); }
      kpi('Magnification', Math.round(mag) + '×', maxU && mag > maxU ? 'over useful ceiling' : '');
      kpi('Exit pupil', exit != null ? fmt(exit, 2) + '<small> mm</small>' : '—', exit != null && exit < 0.5 ? 'very tight' : '');
      kpi('At this power', judged, '');
      $('dsc-canvas-cap').innerHTML = 'Simulated view at <b>' + Math.round(mag) + '×</b> (' + info.label + '). ' + (sc.obs > 0.15 ? 'Obstruction ring, ' : '') + 'brightness ratio, seeing blur and the eye’s own limit at this magnification are all modelled.';
    }

    /* ================================================================
     * ALADIN real-sky preview
     * ============================================================== */
    var aladin = null, fovOverlay = null, aladinStarted = false, aladinCbs = [];
    function initAladin(cb) {
      if (aladin) { cb(true); return; }
      if (typeof A === 'undefined' || !A || !A.init) { cb(false); return; }
      aladinCbs.push(cb);
      if (aladinStarted) return;
      aladinStarted = true;
      A.init.then(function () {
        try {
          aladin = A.aladin('#dsc-aladin', {
            survey: 'P/DSS2/color', fov: 1, projection: 'SIN', cooFrame: 'ICRS',
            showReticle: true, showZoomControl: true, showFullscreenControl: true,
            showLayersControl: false, showGotoControl: false, showCooGridControl: false,
            showSimbadPointerControl: false, showShareControl: false, showFrame: false
          });
          fovOverlay = A.graphicOverlay({ color: '#ff5a5a', lineWidth: 2 });
          aladin.addOverlay(fovOverlay);
          aladinCbs.forEach(function (f) { f(true); }); aladinCbs = [];
        } catch (e) { aladinCbs.forEach(function (f) { f(false); }); aladinCbs = []; }
      }).catch(function () { aladinCbs.forEach(function (f) { f(false); }); aladinCbs = []; });
    }
    function showSky(raHours, decDeg, fovDeg, circleArcmin) {
      initAladin(function (okAladin) {
        var box = $('dsc-aladin'), fb = $('dsc-aladin-fallback');
        if (!okAladin) { if (box) box.style.display = 'none'; if (fb) fb.style.display = 'block'; return; }
        if (box) box.style.display = 'block'; if (fb) fb.style.display = 'none';
        var raDeg = raHours * 15;
        aladin.gotoRaDec(raDeg, decDeg);
        aladin.setFov(clamp(fovDeg * 2.2, 0.02, 60));
        fovOverlay.removeAll();
        fovOverlay.add(A.circle(raDeg, decDeg, fovDeg / 2, { color: '#ff5a5a', lineWidth: 2 }));
        if (circleArcmin) fovOverlay.add(A.circle(raDeg, decDeg, circleArcmin / 120, { color: 'rgba(255,255,255,0.4)', lineWidth: 1 }));
      });
    }

    /* ================================================================
     * RESOLVE TAB
     * ============================================================== */
    function seeingLabel(s) {
      if (s <= 0.75) return 'excellent (Pickering 8–10)';
      if (s <= 1.5) return 'good (Pickering 6–7)';
      if (s <= 2.5) return 'average (Pickering 4–5)';
      if (s <= 4) return 'poor (Pickering 2–3)';
      return 'very poor (Pickering 1)';
    }
    var VERDICT = {
      clean:     { t: 'Clean split', cls: 'ok',   d: 'Dark sky cleanly separates the two stars — a comfortable, repeatable split.' },
      split:     { t: 'Split — but close', cls: 'ok', d: 'Resolvable as two stars, though the gap is tight. Steady moments help.' },
      touching:  { t: 'Touching disks', cls: 'warn', d: 'The disks kiss with little or no dark gap — a figure-eight or notched pair.' },
      elongated: { t: 'Elongated / notched', cls: 'warn', d: 'Not split, but clearly non-round — an elongated blob in moments of good seeing.' },
      single:    { t: 'Single — not resolvable', cls: 'bad', d: 'Under these conditions the pair reads as one star. Something has to change.' }
    };

    function onCalc() {
      var sc = readScope();
      if (!sc.ap) { alert('Please enter your telescope aperture.'); return; }
      var sep = num('dsc-sep'), m1 = num('dsc-m1'), m2 = num('dsc-m2');
      if (sep == null || m1 == null || m2 == null) { alert('Please enter the separation and both magnitudes.'); return; }
      var seeing = num('dsc-seeing');
      var exp = parseFloat($('dsc-exp').value);
      var color = parseFloat($('dsc-color').value);

      // altitude (optional)
      var lat = num('dsc-lat'), lon = num('dsc-lon');
      var radec = root.getAttribute('data-radec');
      var altInfo = null, alt = null;
      if (lat != null && lon != null && radec) {
        var parts = radec.split(','), ra = parseFloat(parts[0]), dec = parseFloat(parts[1]);
        var when = $('dsc-datetime').value ? new Date($('dsc-datetime').value) : new Date();
        if (isNaN(when.getTime())) when = new Date();
        var aa = E.altAz(ra, dec, lat, lon, when);
        var tr = E.transit(ra, dec, lat, lon, when);
        alt = aa.alt; altInfo = { aa: aa, tr: tr, when: when, ra: ra, dec: dec };
      }

      var res = M.resolve({ ap: sc.ap, fl: sc.fl, obs: sc.obs, seeing: seeing, sep: sep, m1: m1, m2: m2, color: color, exp: exp, alt: alt });

      // verdict banner
      var v = VERDICT[res.verdict];
      $('dsc-verdict').className = 'dsc-verdict dsc-' + v.cls;
      $('dsc-verdict').innerHTML = '<span class="dsc-vk">' + v.t + '</span><span class="dsc-vd">' + v.d + '</span>';

      // KPIs
      var kp = $('dsc-kpis'); kp.innerHTML = '';
      function kpi(l, val, sub) { kp.appendChild(h('<div class="dsc-kpi"><div class="dsc-l">' + l + '</div><div class="dsc-v">' + val + '</div><div class="dsc-d">' + (sub || '') + '</div></div>')); }
      kpi('Dawes limit', fmt(res.dawes, 2) + '<small>&Prime;</small>', 'theoretical, equal pair');
      kpi('Rayleigh limit', fmt(res.rayleigh, 2) + '<small>&Prime;</small>', 'first-null criterion');
      kpi('Your effective limit', fmt(res.effectiveRes, 2) + '<small>&Prime;</small>', 'seeing + glare + optics');
      kpi('This pair', fmt(sep, 1) + '<small>&Prime;</small>', 'Δm ' + fmt(res.dm, 1) + ' · ' + (res.ratio >= 1 ? fmt(res.ratio, 2) + '× over limit' : fmt(res.ratio, 2) + '× of limit'));

      // explanation
      var limiterTxt = {
        aperture: 'Your <b>aperture</b> is the main constraint here — the pair is close to the diffraction limit. More aperture (or waiting for the steadiest air) is what helps.',
        seeing: 'The <b>atmosphere</b> is the wall tonight, not your telescope. The seeing disk (' + fmt(res.seeingEff, 1) + '&Prime;) is wider than the pair. A steadier night — or a higher target altitude — matters more than aperture.',
        magnitude: 'The <b>brightness difference</b> (Δm ' + fmt(res.dm, 1) + ') is the real problem: the faint companion sits inside the primary’s glare. Your aperture resolves the geometry easily (' + (res.withinAperture ? 'sep is ' + fmt(sep / res.dawes, 0) + '× the Dawes limit' : 'near Dawes') + '), but contrast — not resolution — is the limiter. This is the Sirius&nbsp;B situation.'
      };
      var expl = limiterTxt[res.limiter];
      if (altInfo && altInfo.aa.alt < 25 && altInfo.aa.alt > 0) expl += ' At only ' + fmt(altInfo.aa.alt, 0) + '° altitude right now, thick air is degrading the view — catch it near its highest point.';
      $('dsc-explain').innerHTML = expl;

      // altitude panel
      var ap = $('dsc-alt');
      if (altInfo) {
        var maxAlt = altInfo.tr.maxAlt;
        var tstr = altInfo.tr.transit.toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
        var altNow = altInfo.aa.alt;
        var quality = maxAlt < 20 ? 'This target stays low and is always fighting the atmosphere from your latitude.' :
          maxAlt < 40 ? 'It never gets very high from your latitude — plan for its transit and expect softened seeing.' :
            'It climbs high enough for good views — aim for the hours around transit.';
        ap.style.display = '';
        ap.innerHTML =
          '<div class="dsc-reco">' +
          '<div class="dsc-box"><h4>Altitude now</h4><span class="dsc-big">' + (altNow > 0 ? fmt(altNow, 0) + '°' : 'below horizon') + '</span><p>airmass ' + (altNow > 0 ? fmt(1 / Math.sin(altNow * DEG), 2) : '—') + '</p></div>' +
          '<div class="dsc-box"><h4>Best (transit)</h4><span class="dsc-big">' + tstr + '</span><p>crosses the meridian, its highest point</p></div>' +
          '<div class="dsc-box"><h4>Max altitude</h4><span class="dsc-big">' + fmt(maxAlt, 0) + '°</span><p>' + (altInfo.tr.circumpolar ? 'circumpolar — never sets' : 'at transit') + '</p></div>' +
          '<div class="dsc-box"><h4>Verdict</h4><span class="dsc-big">' + (maxAlt < 20 ? 'Low' : maxAlt < 40 ? 'Moderate' : 'Good') + '</span><p>' + quality + '</p></div>' +
          '</div>';
      } else {
        ap.style.display = '';
        ap.innerHTML = '<div class="dsc-note">Add your latitude/longitude (and pick a named double) to get tonight’s best observing time, altitude and airmass.</div>';
      }

      // magnification + eyepiece recommendation
      var recMag = res.recMag;
      var epRec = recommendEyepiece(sc, recMag);
      var mg = $('dsc-magrec');
      mg.innerHTML =
        '<div class="dsc-reco">' +
        '<div class="dsc-box"><h4>Suggested magnification</h4><span class="dsc-big">' + recMag + '×</span><p>enlarges the pair to a comfortable angular gap</p></div>' +
        '<div class="dsc-box"><h4>Exit pupil</h4><span class="dsc-big">' + fmt(M.exitPupil(sc.ap, recMag), 2) + ' mm</span><p>' + (M.exitPupil(sc.ap, recMag) < 0.5 ? 'very small — near the useful ceiling' : 'workable') + '</p></div>' +
        '<div class="dsc-box"><h4>Max useful mag</h4><span class="dsc-big">' + Math.round(M.maxUsefulMag(sc.ap)) + '×</span><p>~0.5 mm exit pupil for your aperture</p></div>' +
        '<div class="dsc-box"><h4>Focal length needed</h4><span class="dsc-big">' + (sc.fl ? fmt(sc.fl / recMag, 1) + ' mm' : '—') + '</span><p>' + (sc.fl ? 'eyepiece (before any Barlow)' : 'enter focal length') + '</p></div>' +
        '</div>';

// (store eyepiece card is rendered by renderSim below)

      // synthetic render + eyepiece-driven view. The store eyepiece card
      // (dsc-eprec) is driven by renderSim so it always mirrors the eyepiece
      // chosen in the "See it through an eyepiece" dropdown.
      var recIdx = (epRec && epRec.e) ? EYEPIECES.indexOf(epRec.e) : -1;
      simCtx = { sc: sc, res: res, sep: sep, m1: m1, m2: m2, v: v, recMag: recMag };
      ensureSimControls();
      fillSimEyepieces($('dsc-sim-ep'), recIdx);
      if (recIdx >= 0) $('dsc-sim-ep').value = String(recIdx);
      $('dsc-sim-fl').value = '';
      renderSim();

      $('dsc-results').style.display = 'block';
      $('dsc-results').scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function recommendEyepiece(sc, targetMag) {
      if (!sc.fl) return null;
      var best = null, bestErr = 1e9;
      EYEPIECES.forEach(function (e) {
        if (e.fl === 'zoom') return;
        var mag = sc.fl / e.fl;
        var err = Math.abs(mag - targetMag) / targetMag + (e.st ? 0 : 0.15); // prefer in-stock
        if (err < bestErr) { bestErr = err; best = { e: e, mag: mag }; }
      });
      return best;
    }

    function epCard(e, sc) {
      var flTxt = e.fl === 'zoom' ? (e.flMin + '–' + e.flMax + ' mm') : (e.fl + ' mm');
      var mag = sc && sc.fl && e.fl !== 'zoom' ? Math.round(sc.fl / e.fl) : null;
      var ep = mag ? fmt(M.exitPupil(sc.ap, mag), 2) : null;
      return '<a class="dsc-prodcard" href="' + e.u + '" target="_blank" rel="noopener">' +
        '<img loading="lazy" src="' + e.img + '" alt="' + e.b + ' ' + e.s + ' ' + flTxt + '">' +
        '<div><h4>' + e.b + ' ' + e.s + ' · ' + flTxt + '</h4>' +
        '<p>' + e.afov + '° field' + (mag ? ' · ' + mag + '× · ' + ep + ' mm exit pupil' : '') + '</p>' +
        '<p class="dsc-price">$' + fmt(e.px, 2) + ' CAD · ' + (e.st ? 'in stock' : 'backorder') + '</p></div></a>';
    }

    /* ================================================================
     * EYEPIECE & VIEW TAB
     * ============================================================== */
    function readEp() {
      if (epSel.value === 'manual') {
        var fl = num('dsc-epfl'), afov = num('dsc-epafov');
        if (fl == null || afov == null) return null;
        return { b: 'Your eyepiece', s: '', fl: fl, afov: afov, u: null, img: null, px: null, st: 1 };
      }
      var e = EYEPIECES[parseInt(epSel.value, 10)];
      if (e && e.fl === 'zoom') { return Object.assign({}, e, { fl: Math.round((e.flMin + e.flMax) / 2) }); }
      return e;
    }
    function toggleManualEp() {
      var show = epSel.value === 'manual' ? '' : 'none';
      $('dsc-manual-ep').style.display = show;
      $('dsc-manual-ep2').style.display = show;
    }
    epSel.addEventListener('change', toggleManualEp);

    function onEp() {
      var sc = readScope();
      if (!sc.ap || !sc.fl) { alert('Enter your telescope aperture and focal length first.'); return; }
      var e = readEp();
      if (!e) { alert('Pick an eyepiece or enter its focal length and field.'); return; }

      var mag = M.magnification(sc.fl, e.fl);
      var tfovDeg = M.tfov(e.afov, mag);
      var exit = M.exitPupil(sc.ap, mag);
      var maxU = M.maxUsefulMag(sc.ap);

      var out = $('dsc-epout'); out.innerHTML = '';
      function kpi(l, val, sub) { out.appendChild(h('<div class="dsc-kpi"><div class="dsc-l">' + l + '</div><div class="dsc-v">' + val + '</div><div class="dsc-d">' + (sub || '') + '</div></div>')); }
      kpi('Magnification', Math.round(mag) + '×', (mag > maxU ? 'over the useful ceiling' : 'within useful range'));
      kpi('True field of view', fmt(tfovDeg * 60, 1) + '<small>′</small>', fmt(tfovDeg, 2) + '° of sky');
      kpi('Exit pupil', fmt(exit, 2) + '<small> mm</small>', exit > 5 ? 'may vignette / sky too bright' : exit < 0.5 ? 'very tight — dim & fussy' : 'comfortable');
      kpi('Full Moons across', fmt(tfovDeg * 60 / 31, 2) + '<small>×</small>', 'the Moon is 31′ wide');

      // comparison table across the catalogue
      var rows = EYEPIECES.map(function (x) {
        var fl = x.fl === 'zoom' ? (x.flMin + x.flMax) / 2 : x.fl;
        var m = sc.fl / fl;
        return { x: x, fl: fl, m: m, tfov: x.afov / m, exit: sc.ap / m };
      }).sort(function (a, b) { return a.m - b.m; });
      var tbl = '<thead><tr><th>Eyepiece</th><th>Mag</th><th>True field</th><th>Exit pupil</th><th>Best for</th><th></th></tr></thead><tbody>';
      rows.forEach(function (r) {
        var use = r.exit > 4 ? 'Rich-field, large DSOs' : r.exit > 2 ? 'Deep-sky, clusters' : r.exit > 1 ? 'Galaxies, general' : r.exit > 0.6 ? 'Planets, doubles' : 'Tight doubles (steady air)';
        var flTxt = r.x.fl === 'zoom' ? (r.x.flMin + '–' + r.x.flMax) : r.x.fl;
        var over = r.m > maxU;
        tbl += '<tr' + (r.x === e || (e && r.x.u === e.u) ? ' class="dsc-hl"' : '') + '><td>' + r.x.b + ' ' + r.x.s + ' ' + flTxt + 'mm · ' + r.x.afov + '°</td>' +
          '<td class="dsc-mono">' + Math.round(r.m) + '×' + (over ? ' <span class="dsc-flag dsc-bad">!</span>' : '') + '</td>' +
          '<td class="dsc-mono">' + fmt(r.tfov * 60, 1) + '′</td>' +
          '<td class="dsc-mono">' + fmt(r.exit, 2) + '</td>' +
          '<td>' + use + '</td>' +
          '<td>' + (r.x.u ? '<a href="' + r.x.u + '" target="_blank" rel="noopener">view</a>' : '') + '</td></tr>';
      });
      tbl += '</tbody>';
      $('dsc-eptable').innerHTML = tbl;

      // real-sky preview of the framing target
      var fr = frSel.value, tRa, tDec, tName, tSize = null;
      if (fr.indexOf('dso:') === 0) { var d = DSOS.filter(function (x) { return x.id === fr.slice(4); })[0]; tRa = d.ra; tDec = d.dec; tName = d.name; tSize = d.size; }
      else { var db = DOUBLES.filter(function (x) { return x.id === fr.slice(4); })[0]; tRa = db.ra; tDec = db.dec; tName = db.name; }
      showSky(tRa, tDec, tfovDeg, tSize);
      var fit = tSize ? (tSize <= tfovDeg * 60 ? 'fits inside the field (' + fmt(tfovDeg * 60 / tSize, 1) + '× room)' : 'is larger than the field — you’ll see part of it') : 'is a point pair at this scale';
      $('dsc-sky-cap').innerHTML = 'Red circle = your true field with this eyepiece (' + fmt(tfovDeg * 60, 1) + '′). <b>' + tName + '</b> ' + fit + '. Imagery: DSS via Aladin Lite (CDS).';

      $('dsc-ep-results').style.display = 'block';
    }

    /* ---- tabs ------------------------------------------------------- */
    function tab(name) {
      ['resolve', 'ep'].forEach(function (n) {
        $('dsc-tabbtn-' + n).classList.toggle('dsc-on', n === name);
        $('dsc-panel-' + n).style.display = n === name ? 'block' : 'none';
      });
      if (name === 'ep') { toggleManualEp(); }
    }
    $('dsc-tabbtn-resolve').addEventListener('click', function () { tab('resolve'); });
    $('dsc-tabbtn-ep').addEventListener('click', function () { tab('ep'); });

    $('dsc-calc').addEventListener('click', onCalc);
    $('dsc-ep-calc').addEventListener('click', onEp);
    $('dsc-reset').addEventListener('click', function () {
      $('dsc-results').style.display = 'none'; tgtSel.value = 'sirius'; applyTarget();
    });

    // default datetime = now (local, for the datetime-local input)
    (function () {
      var n = new Date(); n.setMinutes(n.getMinutes() - n.getTimezoneOffset());
      $('dsc-datetime').value = n.toISOString().slice(0, 16);
    })();

    // sensible defaults so first-time users see a result immediately
    tgtSel.value = 'sirius'; applyTarget();
    (function () { // default eyepiece: in-stock one nearest 20 mm, else nearest 20 mm
      var bi = 0, be = 1e9;
      EYEPIECES.forEach(function (e, i) {
        if (e.fl === 'zoom') return;
        var err = Math.abs(e.fl - 20) + (e.st ? 0 : 6);
        if (err < be) { be = err; bi = i; }
      });
      epSel.value = String(bi);
    })();
  });

})();
